
import * as React from "react"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Mail } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "@/context/AuthContext"

export function ContactDialog() {
  const [message, setMessage] = useState("")
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const { toast } = useToast()
  const [isOpen, setIsOpen] = useState(false)
  const [feedbackType, setFeedbackType] = useState("bug")
  const [isSending, setIsSending] = useState(false)
  const { user } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return

    // Use authenticated user's info if available, otherwise use form inputs
    const senderName = user ? (user.user_metadata?.full_name || user.email) : name
    const senderEmail = user?.email || email

    if (!senderEmail || (!user && !name)) {
      toast({
        title: "Missing information",
        description: "Please provide your name and email.",
        variant: "destructive",
      })
      return
    }

    setIsSending(true)
    try {
      const { error } = await supabase.functions.invoke("send-email", {
        body: {
          to: "hellomle25@gmail.com",
          subject: `Contact Form - ${feedbackType}`,
          html: `
            <h2>New Contact Form Submission</h2>
            <p><strong>Type:</strong> ${feedbackType}</p>
            <p><strong>From:</strong> ${senderName} (${senderEmail})</p>
            <p><strong>User Status:</strong> ${user ? 'Authenticated User' : 'Guest User'}</p>
            ${user ? `<p><strong>User ID:</strong> ${user.id}</p>` : ''}
            <p><strong>Message:</strong></p>
            <p>${message.replace(/\n/g, "<br/>")}</p>
          `,
          text: `New Contact Form Submission\n\nType: ${feedbackType}\nFrom: ${senderName} (${senderEmail})\nUser Status: ${user ? 'Authenticated User' : 'Guest User'}\n${user ? `User ID: ${user.id}\n` : ''}\nMessage:\n${message}`,
          replyTo: senderEmail,
        },
      })

      if (error) throw error

      toast({
        title: "Message sent",
        description: `Thank you for your ${feedbackType} submission. We'll get back to you soon.`,
      })
      setMessage("")
      setName("")
      setEmail("")
      setIsOpen(false)
    } catch (error) {
      console.error("Error sending contact message:", error)
      toast({
        title: "Error",
        description: "Failed to send message. Please try again later.",
        variant: "destructive",
      })
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <button className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
          <Mail className="h-3.5 w-3.5" /> Contact Us
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send us a message</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select value={feedbackType} onValueChange={setFeedbackType}>
            <SelectTrigger>
              <SelectValue placeholder="Select feedback type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bug">Bug Report</SelectItem>
              <SelectItem value="feedback">User Feedback</SelectItem>
            </SelectContent>
          </Select>
          
          {!user && (
            <>
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium mb-1">
                  Name
                </label>
                <Input
                  id="contact-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium mb-1">
                  Email
                </label>
                <Input
                  id="contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  required
                />
              </div>
            </>
          )}
          
          {user && (
            <div className="bg-muted/30 p-3 rounded border">
              <p className="text-sm text-muted-foreground">
                Sending as: <strong>{user.user_metadata?.full_name || user.email}</strong>
              </p>
            </div>
          )}
          
          <Textarea
            placeholder="Type your message here..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="min-h-[120px]"
          />
          <Button type="submit" disabled={!message.trim() || isSending}>
            {isSending ? "Sending..." : "Send Message"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
